import { useEffect, useRef } from "react";
import SignaturePad from "react-signature-pad-wrapper";
import { PointGroup } from "signature_pad";
import { SignatureType } from "@newComponents/common/Viewer/types";
import { handleDownloadUrlResponse } from "@newLibs/converting";
import { useBoolean } from "./useBoolean";
import dayjs from "dayjs";

export function useDrawingRefTrace(signatureType?: SignatureType) {
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const signaturePadRedoUndoStack = useRef<Array<PointGroup>>([]);

  const isSignatureEmpty = useBoolean(true);
  const isRedoStackEmpty = useBoolean(true);
  const isFirstDrawingStarted = useBoolean(false);

  useEffect(() => {
    if (signatureType && signatureType === SignatureType.Stamp) return;

    setTimeout(() => {
      const signaturePad = signaturePadRef.current;

      if (!signaturePad) return;

      const handleDrawingStart = () => {
        if (!isFirstDrawingStarted.value) {
          isFirstDrawingStarted.onTrue();
        }
      };

      const handleDrawingEnd = () => {
        if (!signaturePad || signaturePad.isEmpty()) return;

        const drawingPGList = signaturePad.toData(); // PG means PointerGroup

        if (drawingPGList && drawingPGList.length) {
          signaturePadRedoUndoStack.current = [...drawingPGList];
        }

        isSignatureEmpty.value && isSignatureEmpty.onFalse();
        isRedoStackEmpty.onTrue();
      };

      signaturePad.instance.addEventListener("beginStroke", handleDrawingStart);
      signaturePad.instance.addEventListener("endStroke", handleDrawingEnd);

      return () => {
        signaturePad.instance.removeEventListener("beginStroke", handleDrawingStart);
        signaturePad.instance.removeEventListener("endStroke", handleDrawingEnd);
      };
    }, 50);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureType]);

  const handleUndo = () => {
    const signaturePad = signaturePadRef.current;
    if (!signaturePad || signaturePad.isEmpty()) return;

    const data = signaturePad?.toData();
    if (data && data.length) {
      data.pop();

      signaturePad.fromData(data);

      if (signaturePad.isEmpty()) {
        isSignatureEmpty.onTrue();
        isFirstDrawingStarted.onFalse();
      }

      isRedoStackEmpty.value && isRedoStackEmpty.onFalse();
    }
  };

  const handleRedo = () => {
    const signaturePad = signaturePadRef.current;

    if (!signaturePadRedoUndoStack.current.length) return;
    if (!signaturePad) return;

    const stack = signaturePadRedoUndoStack.current;
    const originalPGList = signaturePad.toData();
    const toRedoPGIdx = originalPGList.length;

    if (stack.length === toRedoPGIdx) {
      return;
    }

    const redoPG = { ...stack[toRedoPGIdx] };

    if (redoPG) {
      signaturePad.fromData([...originalPGList, redoPG]);
      isSignatureEmpty.value && isSignatureEmpty.onFalse();

      const isRedoUndoStackSameWithOriginSignature = stack.length - 1 === toRedoPGIdx;
      !isFirstDrawingStarted.value && isFirstDrawingStarted.onTrue();

      if (isRedoUndoStackSameWithOriginSignature) {
        !isRedoStackEmpty.value && isRedoStackEmpty.onTrue();
      }
    }
  };

  const handleClear = () => {
    const signaturePad = signaturePadRef.current;

    if (!signaturePad) return;

    !isSignatureEmpty.value && isSignatureEmpty.onTrue();
    !isRedoStackEmpty.value && isRedoStackEmpty.onTrue();
    isFirstDrawingStarted.value && isFirstDrawingStarted.onFalse();
    signaturePad.clear();
  };

  const handleSignatureDownload = () => {
    const signaturePad = signaturePadRef.current;
    if (!signaturePad || signaturePad.isEmpty()) {
      return;
    }

    const now = dayjs().format("YYYY-MM-DD-HH-mm-ss");

    const canvas = signaturePad.canvas.current;
    if (!canvas) return;

    const temp = canvas.toDataURL("image/png");

    if (!temp) return;

    handleDownloadUrlResponse(temp, `signature-${now}.png`);
  };

  return {
    signaturePadRef,
    isSignatureEmpty: isSignatureEmpty.value,
    isRedoStackEmpty: isRedoStackEmpty.value,
    isFirstDrawingStarted: isFirstDrawingStarted.value,
    onClickClear: handleClear,
    onClickRedo: handleRedo,
    onClickUndo: handleUndo,
    onClickSignatureDownload: handleSignatureDownload,
  };
}
